import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Paperclip, X, Loader2, AlertCircle } from 'lucide-react';
import type { ComposeAttachment } from '@/types/compose';
import { ComposeAttachmentPreviewDialog } from './ComposeAttachmentPreviewDialog';

interface ComposeAttachmentsProps {
  attachments: ComposeAttachment[];
  onRemoveAttachment: (index: number) => void;
  onFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onAppendFiles: (files: FileList | File[]) => void;
  showList?: boolean;
  showDropzone?: boolean;
}

export function ComposeAttachments({
  attachments,
  onRemoveAttachment,
  onFileInputChange,
  onAppendFiles,
  showList = true,
  showDropzone = true,
}: ComposeAttachmentsProps) {
  const [isDragging, setIsDragging] = useState(false);
  // A snapshot, not an id: a draft save swaps local ids for server ids, which
  // would otherwise close the preview mid-look.
  const [previewAttachment, setPreviewAttachment] = useState<ComposeAttachment | null>(null);

  const hasContent = (showList && attachments.length > 0) || showDropzone;
  
  if (!hasContent) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 shrink-0">
      {showList && attachments.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attachments.map((attachment, index) => (
            <div
              key={attachment.id}
              className="flex items-center gap-1 rounded-full border border-border/60 bg-muted/30 px-2 py-0.5 text-xs"
            >
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setPreviewAttachment(attachment)}
                className="flex min-w-0 items-center gap-1 hover:text-foreground hover:underline"
                title={`Preview ${attachment.name}`}
              >
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="max-w-[160px] truncate">{attachment.name}</span>
              </button>
              {attachment.status === 'pending' || attachment.status === 'uploading' ? (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              ) : null}
              {attachment.status === 'error' && (
                <span
                  className="text-destructive"
                  role="img"
                  aria-label={attachment.error ?? 'Attachment failed to save'}
                >
                  <AlertCircle className="h-3 w-3" aria-hidden="true" />
                </span>
              )}
              <button
                type="button"
                tabIndex={-1}
                onClick={() => onRemoveAttachment(index)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ComposeAttachmentPreviewDialog
        attachment={previewAttachment}
        onOpenChange={(open) => {
          if (!open) setPreviewAttachment(null);
        }}
      />

      {showDropzone && (
        <div
          className={cn('relative inline-flex rounded transition-colors', isDragging && 'bg-primary/10')}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const files = event.dataTransfer?.files;
            if (files && files.length > 0) {
              onAppendFiles(files);
            }
            setIsDragging(false);
          }}
        >
          <Button variant="ghost" size="sm" tabIndex={-1} className={cn('h-7 px-2 gap-1', isDragging && 'bg-primary/10')} asChild>
            <label className="cursor-pointer" tabIndex={-1}>
              <Paperclip className="h-3.5 w-3.5" />
              <input type="file" className="hidden" multiple onChange={onFileInputChange} />
            </label>
          </Button>
        </div>
      )}
    </div>
  );
}
